import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import API from '../../api';
// import { Button } from '../../components';
import { Button } from '@mui/material';
import { SnackbarContext } from '../../contexts';
import { FormInput } from './FormInput';

/**
 * react-hook-form과 함께 사용하는 FormEvidence 컴포넌트
 * @param {Object} props
 * @param {Object} props.control - react-hook-form의 control 객체
 * @param {string} props.name - 필드 이름 (예: 'evidence')
 * @param {Array} props.labels - 증빙서류 라벨 배열 [{title: string, isForce: boolean}]
 * @param {Object} props.error - 에러 객체
 * @param {boolean} props.disabled - 비활성화 여부
 * @param {boolean} props.readOnly - 읽기 전용 여부
 * @param {Object} props.style - 스타일 객체
 * @param {string} props.className - 클래스명
 */
export const FormEvidence = (props) => {
  const {
    control,
    name = 'evidence',
    updatedEvidence,
    labels = [],
    error,
    rules,
    disabled,
    readOnly,
    style,
    className,
  } = props;
  const { t } = useTranslation();
  const initializedRef = useRef(false);
  const labelsKeyRef = useRef(JSON.stringify(labels.map((l) => l.seq)));

  // labels가 변경되면 초기화 플래그 리셋
  useEffect(() => {
    const currentKey = JSON.stringify(labels.map((l) => l.seq));
    if (labelsKeyRef.current !== currentKey) {
      labelsKeyRef.current = currentKey;
      initializedRef.current = false;
    }
  }, [labels]);

  if (!labels || labels.length === 0) {
    return null;
  }

  const renderHeader = (index) => {
    const label = labels[index];
    if (!label) return null;

    return (
      <header className='mb-3 flex items-center gap-2'>
        <span className='text-base font-semibold text-gray-800'>{label.title}</span>
        {label.isForce && <span className='text-red-600'>*</span>}
      </header>
    );
  };

  const renderRadioBox = (index, fieldValue, onChange) => {
    const label = labels[index];
    if (!label || label.isForce) {
      return null;
    }

    return (
      <div className='mb-4'>
        <RadioGroup
          row
          value={fieldValue?.type || 'File'}
          onChange={(e) => {
            const newType = e.target.value;
            onChange({
              seq: fieldValue?.seq || label.seq || index,
              type: newType,
              data: '', // 타입 변경 시 데이터 초기화
            });
          }}
        >
          <FormControlLabel value='File' control={<Radio disabled={disabled || readOnly} />} label={t('DO_SUBMIT')} />
          <FormControlLabel
            value='Reason'
            control={<Radio disabled={disabled || readOnly} />}
            label={t('NOT_DO_SUBMIT')}
          />
        </RadioGroup>
      </div>
    );
  };

  const renderFileBox = (index, fieldValue, onChange, fieldError) => {
    const label = labels[index];
    if (fieldValue?.type !== 'File') {
      return null;
    }

    return (
      <div className='mb-4'>
        <EvidenceFileUploader
          value={fieldValue?.data || ''}
          onChange={(fileUrl) => {
            onChange({
              seq: fieldValue?.seq || label.seq || index,
              type: fieldValue?.type || 'File',
              data: fileUrl,
            });
          }}
          disabled={disabled || readOnly}
          readOnly={readOnly}
          index={index}
          error={fieldError}
        />
      </div>
    );
  };

  const renderTextInputBox = (index, fieldValue, onChange, fieldError) => {
    const label = labels[index];
    if (fieldValue?.type !== 'Reason') {
      return null;
    }

    // FormInput은 error 객체를 받으므로, 문자열인 경우 객체로 변환
    const errorObj = fieldError ? (typeof fieldError === 'string' ? { message: fieldError } : fieldError) : undefined;

    return (
      <div className='mb-4'>
        <FormInput
          value={fieldValue?.data || ''}
          onChange={(e) => {
            onChange({
              seq: fieldValue?.seq || label.seq || index,
              type: fieldValue?.type || 'Reason',
              data: e.target.value,
            });
          }}
          placeholder={t('REASON_FOR_NOT_SUPPORTING_DOC')}
          disabled={disabled || readOnly}
          error={errorObj}
        />
      </div>
    );
  };

  // validate 함수: isForce가 true인 항목에 대해 required 검증
  // 각 항목의 에러를 객체로 반환하여 개별적으로 표시할 수 있도록 함
  const validateEvidence = (value) => {
    const evidenceArray = value || [];
    const errors = {};

    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];
      if (label.isForce) {
        const item = evidenceArray[i];
        // isForce가 true인 경우, data가 비어있으면 안됨
        const dataValue = item?.data;
        const isEmpty = !dataValue || (typeof dataValue === 'string' && dataValue.trim() === '');

        if (isEmpty) {
          const errorMessage =
            rules?.required || (label.title || t('SUPPORTING_DOC')) + ' ' + (t('REQUIRED') || '필수 항목입니다.');
          errors[i] = errorMessage;
        }
      }
    }

    // 에러가 있으면 첫 번째 에러 메시지를 반환하거나, 에러 객체를 반환
    if (Object.keys(errors).length > 0) {
      // react-hook-form은 문자열이나 true를 반환해야 하므로, 첫 번째 에러를 반환
      // 하지만 개별 항목 에러는 getItemError에서 처리
      return Object.values(errors)[0];
    }
    return true;
  };

  return (
    <article className={`grid grid-cols-2 gap-6 ${className || ''}`} style={style || {}}>
      <Controller
        control={control}
        name={name}
        defaultValue={[]}
        rules={{
          ...rules,
          validate: validateEvidence,
        }}
        render={({ field, fieldState }) => {
          const evidenceArray = field.value || [];

          // labels 길이에 맞춰 배열 초기화 (항상 field.value 기반으로 재계산)
          const normalizedArray = labels.map((label, index) => {
            const existingItem = evidenceArray[index];
            if (existingItem && existingItem.seq === (label.seq || index)) {
              return existingItem;
            }

            // updatedEvidence에서 해당 evidenceSeq의 data(한번 저장된 데이터)를 찾아서 반환
            return {
              seq: label.seq || index,
              type: label.isForce ? 'File' : 'File', // 기본값
              data: updatedEvidence?.find((el) => el.evidenceSeq === label.seq)?.data || '',
            };
          });

          // 배열이 초기화되지 않았거나 길이가 다르면 업데이트 (한 번만)
          const needsUpdate =
            !initializedRef.current &&
            (evidenceArray.length !== labels.length ||
              normalizedArray.some((item, idx) => {
                const existing = evidenceArray[idx];
                return !existing || existing.seq !== item.seq;
              }));

          if (needsUpdate) {
            initializedRef.current = true;
            // React의 상태 업데이트 배치를 고려하여 즉시 업데이트
            field.onChange(normalizedArray);
          }

          // 개별 항목 업데이트 함수 (항상 최신 field.value 사용)
          const updateItem = (index, newItem) => {
            // 항상 최신 field.value를 사용
            const currentArray = field.value || [];
            const newArray = [...currentArray];
            // 배열 길이 보장
            while (newArray.length <= index) {
              newArray.push({ seq: index, type: 'File', data: '' });
            }
            newArray[index] = newItem;
            field.onChange(newArray);
          };

          // 개별 항목 에러 추출
          const getItemError = (index) => {
            const label = labels[index];
            if (!label?.isForce) {
              return null;
            }

            // 해당 항목이 실제로 비어있는지 확인
            const item = normalizedArray[index];
            const dataValue = item?.data;
            const isEmpty = !dataValue || (typeof dataValue === 'string' && dataValue.trim() === '');

            // isForce이고 비어있는 경우 에러 반환
            if (isEmpty) {
              // 에러 메시지 생성
              const errorMessage =
                rules?.required || (label.title || t('SUPPORTING_DOC')) + ' ' + (t('REQUIRED') || '필수 항목입니다.');

              // fieldState.error가 있으면 (검증이 실행된 경우) 에러 메시지 반환
              if (fieldState.error) {
                // 에러가 문자열인 경우 (validateEvidence가 반환한 첫 번째 에러)
                // 모든 비어있는 isForce 항목에 에러 표시
                if (typeof fieldState.error === 'string') {
                  return errorMessage;
                }
                // 배열 형태의 에러인 경우
                if (fieldState.error[index]) {
                  return typeof fieldState.error[index] === 'string'
                    ? fieldState.error[index]
                    : fieldState.error[index]?.message || errorMessage;
                }
                // 객체 형태의 에러인 경우
                if (fieldState.error[index]) {
                  return typeof fieldState.error[index] === 'string'
                    ? fieldState.error[index]
                    : fieldState.error[index]?.message || errorMessage;
                }
                // fieldState.error가 있으면 모든 비어있는 isForce 항목에 에러 표시
                return errorMessage;
              }
              // fieldState.error가 없어도, 필드가 터치되었거나 변경되었으면 에러 표시
              // (실시간 검증을 위해)
              if (fieldState.isTouched || fieldState.isDirty) {
                return errorMessage;
              }
            }
            return null;
          };

          return (
            <>
              {normalizedArray.map((item, index) => {
                const itemError = getItemError(index);
                return (
                  <div
                    key={`evidence-item-${index}`}
                    className='border border-gray-200 rounded-lg p-6 bg-white shadow-sm hover:shadow-md transition-shadow'
                  >
                    {renderHeader(index)}
                    {renderRadioBox(index, item, (newItem) => updateItem(index, newItem))}
                    {renderFileBox(index, item, (newItem) => updateItem(index, newItem), itemError)}
                    {renderTextInputBox(index, item, (newItem) => updateItem(index, newItem), itemError)}
                  </div>
                );
              })}
              {error && <FormHelperText error>{typeof error === 'object' ? error.message : error}</FormHelperText>}
            </>
          );
        }}
      />
    </article>
  );
};

/**
 * 증빙서류 파일 업로더 컴포넌트
 */
const EvidenceFileUploader = ({ value, onChange, disabled, readOnly, index, error }) => {
  const { t } = useTranslation();
  const { updateSnackbar } = useContext(SnackbarContext);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);

  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  }, []);

  React.useEffect(() => {
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', 'project/evidence');

      setLoading(true);
      API.Common.PostFile(formData)
        .then((res) => {
          const { cloudUrl } = res.data;
          onChange(cloudUrl);
          setLoading(false);
          // 파일 입력 초기화
          const input = document.getElementById(`evidence-file-${index}`);
          if (input) {
            input.value = '';
          }
          setFile(null);
        })
        .catch((err) => {
          console.error('File upload error:', err);
          setLoading(false);
          updateSnackbar({
            type: 'error',
            open: true,
            message: err.data?.message || '파일 업로드 중 오류가 발생했습니다.',
          });
          const input = document.getElementById(`evidence-file-${index}`);
          if (input) {
            input.value = '';
          }
          setFile(null);
        });
    }
  }, [file, onChange, index, updateSnackbar]);

  const handleRemove = useCallback(() => {
    onChange('');
    const input = document.getElementById(`evidence-file-${index}`);
    if (input) {
      input.value = '';
    }
  }, [onChange, index]);

  const renderPreview = () => {
    if (!value) {
      return (
        <div className='flex items-center justify-center w-full h-10 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50'>
          <span className='text-sm text-gray-400'>{t('SUPPORTING_DOC') || '파일이 선택되지 않았습니다'}</span>
        </div>
      );
    }

    const fileName = value.split('/').pop();
    return (
      <div className='flex items-center justify-between w-full p-3 bg-gray-50 border border-gray-200 rounded-lg'>
        <span
          onClick={() => {
            window.open(value, '_blank');
          }}
          className='text-sm text-blue-600 hover:text-blue-800 w-full max-w-[240px] cursor-pointer text-ellipsis underline truncate flex-1'
        >
          {fileName}
        </span>
      </div>
    );
  };

  return (
    <div className='flex flex-col gap-1'>
      <div className='flex flex-1 items-center gap-3'>
        <div className={`relative flex-1 ${error ? 'border border-red-500 rounded-lg' : ''}`}>
          {value && (
            <div className='absolute top-2 right-2 z-10'>
              {!disabled && !readOnly && (
                <button
                  type='button'
                  onClick={handleRemove}
                  className='flex items-center justify-center w-6 h-6 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors text-xs font-bold'
                  aria-label='Remove file'
                >
                  ✕
                </button>
              )}
            </div>
          )}
          {renderPreview()}
        </div>

        {!disabled && !readOnly && (
          <div className='flex justify-start'>
            <Button
              variant='contained'
              disableElevation
              color='primary'
              sx={{
                width: '100px',
                height: '40px',
              }}
              onClick={() => {
                document.getElementById(`evidence-file-${index}`)?.click();
              }}
            >
              {t('FILE_CHOOSE')}
            </Button>
          </div>
        )}

        <div className='hidden'>
          <input
            type='file'
            id={`evidence-file-${index}`}
            onChange={handleFileChange}
            disabled={loading || disabled || readOnly}
          />
        </div>
      </div>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </div>
  );
};
